import React, { useState, useEffect, useCallback } from "react";
import { TEXT } from "Clients/ACCB/ACCBTheme";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Button,
  Grid,
  Box,
  Switch,
  Chip,
  Typography,
  TablePagination,
  TableSortLabel,
  makeStyles,
  MenuItem,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import { Edit, Save, Close, Print } from "@material-ui/icons";
import instance from "api/globalaxios";
import CircularCenteredLoader from "features/common/CircularCenteredLoader";
import GlobalSearch from "../../Components/GlobalSearch/GlobalSearch";
import DatePickerACCB from "../../Components/DatePickerACCB";
import { displayEmail, isBacTempEmail, parseDateString, safeDateFormat } from "../../utils";

/**
 * BacMasterPanel
 * -----------------------------------------------------------------------------
 * A generic, config-driven CRUD panel for the BAC masters (Arbitrator, Counsel,
 * Arbitration Stage). These live on the `/bac/masters/...` API namespace and use
 * a different field shape from the TDSAT masters, so they are handled here rather
 * than in the shared MasterTable flow.
 *
 * To add another BAC master, just add an entry to BAC_MASTERS in index.js — no
 * changes needed in this file.
 *
 * Field config shape:
 *   { key, label, type: "text" | "email" | "phone" | "boolean", required, default }
 *
 * NOTE: list + add are the documented endpoints. Modify/Delete assume the
 * standard DRF detail route `${endpoint}${id}/`. Adjust here if the backend
 * differs.
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\d{10}$/;

const useStyles = makeStyles((theme) => ({
  panel: {
    width: "100%",
    // maxWidth: 980,
    margin: "0 auto",
  },
  header: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(2),
    justifyContent: "space-between",
  },
  title: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: TEXT.strong,
  },
  count: {
    color: theme.palette.text.secondary,
    fontSize: "0.81rem",
    marginTop: 2,
  },
  paper: {
    padding: theme.spacing(3),
    borderRadius: 10,
    width: "100%",
    boxSizing: "border-box",
  },
  addForm: {
    // paddingBottom: theme.spacing(2.5),
    marginBottom: theme.spacing(1),
    borderBottom: "1px solid #E4E7EC",
  },
  addButton: {
    borderRadius: 4,
    height: 36,
    width: "100%",
  },
  switchRow: {
    display: "flex",
    alignItems: "center",
    height: "100%",
  },
  tableContainer: {
    boxShadow: "none",
    border: "1px solid #EAECF0",
    borderRadius: 8,
  },
  headCell: {
    fontWeight: 600,
    color: TEXT.strong,
    fontSize: "0.81rem",
    background: "#FAFAFB",
    whiteSpace: "nowrap",
    padding: "6px  10px",
  },
  cell: {
    fontSize: "0.875rem",
    color: TEXT.strong,
    padding: "6px  10px",
  },
  actionBtn: {
    background: "#F4F8FF",
    borderRadius: 4,
    color: TEXT.strong,
    fontSize: "0.8rem",
    padding: "4px 8px",
    gap: 6,
  },
  iconActionBtn: {
    background: "#F4F8FF",
    borderRadius: 4,
    color: TEXT.strong,
    padding: 6,
  },
  emptyState: {
    padding: theme.spacing(5),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  filter: {
    minWidth: 190,
    background: "#fff",
  },
  // Keep the sort control visually identical to the other column headings —
  // TableSortLabel otherwise recolours itself on hover/focus/active.
  sortLabel: {
    color: "inherit",
    fontSize: "inherit",
    fontWeight: "inherit",
    "&:hover": { color: "inherit" },
    "&:focus": { color: "inherit" },
    "&.MuiTableSortLabel-active": { color: "inherit" },
    "& .MuiTableSortLabel-icon": {
      color: "inherit !important",
      fontSize: "1.15em",
      marginLeft: 2,
      marginRight: 0,
    },
  },
}));

const buildEmptyRow = (config) =>
  config.fields.reduce((acc, f) => {
    acc[f.key] = f.type === "boolean" ? (f.default ?? false) : "";
    return acc;
  }, {});

const validateRow = (config, values) => {
  const errors = {};
  config.fields.forEach((f) => {
    const value = values[f.key];
    if (f.type === "boolean") return;
    if (f.required && (value === null || value === undefined || !String(value || "").trim())) {
      errors[f.key] = `${f.label} is required`;
      return;
    }
    if (value && f.type === "email" && !EMAIL_REGEX.test(value)) {
      errors[f.key] = "Enter a valid email address";
    }
    if (value && f.type === "phone" && !PHONE_REGEX.test(value)) {
      errors[f.key] = "Phone number must be 10 digits";
    }
  });
  return errors;
};

const ROWS_PER_PAGE = 10;

const getSearchUrl = (endpoint) => {
  if (endpoint.includes("arbitrator")) {
    return "bac/search/masters/arbitrator/";
  }
  if (endpoint.includes("counsel")) {
    return "bac/search/masters/counsel/";
  }
  if (endpoint.includes("arbitration-stage")) {
    return "bac/search/masters/stage/";
  }
  return null;
};

const BacMasterPanel = ({ config, showSnackbar }) => {
  const classes = useStyles();

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0); // zero-based for TablePagination
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categories, setCategories] = useState([]);

  const [categoryFilter, setCategoryFilter] = useState("all");
  const [ageSort, setAgeSort] = useState(""); // "" | "asc" | "desc"

  const isArbitrator = config.label === "Arbitrator";

  const hasCombinedName =
    config.fields.some((f) => f.key === "first_name") &&
    !config.fields.some((f) => f.key === "last_name");

  const fullName = (row) =>
    [row?.user?.first_name ?? row?.first_name, row?.user?.last_name ?? row?.last_name]
      .filter(Boolean)
      .join(" ");

  useEffect(() => {
    if (!isArbitrator) return;
    const fetchCategories = async () => {
      try {
        const res = await instance.get("bac/arbitrator/category/");
        setCategories(res.data?.results || res.data || []);
      } catch (error) {
        console.error("Error fetching arbitrator categories:", error);
      }
    };
    fetchCategories();
  }, [isArbitrator]);

  // Add-row state
  const [newRow, setNewRow] = useState(buildEmptyRow(config));
  const [addErrors, setAddErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Inline-edit state
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  // Placeholder email of the row being edited, held back so it can be restored
  // on save without ever being rendered. See startEdit/saveEdit.
  const [editTempEmail, setEditTempEmail] = useState(null);

  const detailUrl = (id) => `${config.endpoint}${id}/`;

  const fetchList = useCallback(
    async (
      pageArg = 1,
      searchText = search,
      categoryId = categoryFilter,
      ageOrder = ageSort
    ) => {
      setLoading(true);
      try {
        const params = { page: pageArg };
        if (searchText) params.search = searchText;
        if (categoryId && categoryId !== "all") params.category = categoryId;
        if (ageOrder) params.age = ageOrder;

        const url = searchText
          ? getSearchUrl(config.endpoint) || config.endpoint
          : config.endpoint;

        const res = await instance.get(url, { params });
        const payload = res?.data;
        if (Array.isArray(payload)) {
          setRows(payload);
          setCount(payload.length);
        } else {
          setRows(payload?.results || []);
          setCount(payload?.count ?? (payload?.results?.length || 0));
        }
      } catch (error) {
        console.error(`Error fetching ${config.label}:`, error);
        showSnackbar(`Could not load ${config.label} records.`, "error");
        setRows([]);
        setCount(0);
      } finally {
        setLoading(false);
      }
    },
    [config.endpoint, config.label, showSnackbar, search, categoryFilter, ageSort]
  );

  // Reset and load whenever the selected master changes.
  useEffect(() => {
    setPage(0);
    setSearch("");
    setCategoryFilter("all");
    setAgeSort("");
    setEditId(null);
    setEditRow(null);
    setNewRow(buildEmptyRow(config));
    setAddErrors({});
    fetchList(1, "", "all", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchList(newPage + 1, search);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
    setPage(0);
    fetchList(1, val);
  };

  const handleCategoryFilterChange = (val) => {
    setCategoryFilter(val);
    setPage(0);
    fetchList(1, search, val, ageSort);
  };

  // Column header cycles ascending → descending → unsorted (the default).
  const handleAgeSortToggle = () => {
    const next = ageSort === "" ? "asc" : ageSort === "asc" ? "desc" : "";
    setAgeSort(next);
    setPage(0);
    fetchList(1, search, categoryFilter, next);
  };

  const buildPayload = (values) => {
    const payload = config.fields.reduce((acc, f) => {
      let val = values[f.key];
      if (f.type === "date") {
        acc[f.key] = val instanceof Date && !isNaN(val.getTime()) ? safeDateFormat(val) : val || null;
      } else if (f.type === "boolean") {
        acc[f.key] = Boolean(val);
      } else {
        acc[f.key] = val;
      }
      return acc;
    }, {});

    if (config.endpoint.includes("user")) {
      payload.profile_type = "sub_admin_tiac_rv";
    }

    return payload;
  };

  const handleAdd = async () => {
    const errors = validateRow(config, newRow);
    setAddErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      await instance.post(config.endpoint, buildPayload(newRow));
      showSnackbar(`${config.label} added successfully.`, "success");
      setNewRow(buildEmptyRow(config));
      setAddErrors({});
      setPage(0);
      fetchList(1, search);
    } catch (error) {
      console.error(`Error adding ${config.label}:`, error);
      const apiError =
        error?.response?.data?.error ||
        error?.response?.data?.detail ||
        `Could not add ${config.label}. Please try again.`;
      showSnackbar(
        typeof apiError === "string" ? apiError : `Could not add ${config.label}.`,
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    const flat = { ...row };
    if (row.user) {
      if (flat.first_name === undefined || flat.first_name === null) flat.first_name = row.user.first_name ?? "";
      if (flat.last_name === undefined || flat.last_name === null) flat.last_name = row.user.last_name ?? "";
      if (flat.email === undefined || flat.email === null) flat.email = row.user.email ?? "";
    }
    // The Name input is a single field, so open it with both halves joined —
    // saveEdit writes the whole thing back to first_name.
    if (hasCombinedName) {
      flat.first_name = fullName(row);
    }
    if (flat.arbitator_category === undefined || flat.arbitator_category === null) {
      flat.arbitator_category = row.arbitrator_category ?? "";
    }
    if (flat.age === undefined || flat.age === null) {
      flat.age = row.age ?? "";
    }
    if (flat.experience === undefined || flat.experience === null) {
      flat.experience = row.experience ?? "";
    }
    if (flat.birth_date === undefined || flat.birth_date === null) {
      flat.birth_date = row.birth_date ?? "";
    }
    if (flat.phone_number === undefined || flat.phone_number === null) {
      flat.phone_number = row.phone_number ?? "";
    }
    // Phone-only accounts carry a `bac_temp_email` placeholder instead of a real
    // address. It is internal and must never surface on the platform, so the
    // field opens blank — saveEdit puts the placeholder back if it stays blank.
    if (isBacTempEmail(flat.email)) {
      setEditTempEmail(flat.email);
      flat.email = "";
    } else {
      setEditTempEmail(null);
    }
    setEditRow(flat);
    setEditErrors({});
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditRow(null);
    setEditErrors({});
    setEditTempEmail(null);
  };

  const saveEdit = async () => {
    // A blank Email on a row that only ever had the placeholder means the admin
    // left it untouched — restore it so the save doesn't wipe the account's
    // login identifier, and don't fail the "required" check on the empty field.
    const keepTempEmail =
      Boolean(editTempEmail) && !String(editRow.email || "").trim();
    const values = keepTempEmail
      ? { ...editRow, email: editTempEmail }
      : editRow;

    const errors = validateRow(config, editRow);
    if (keepTempEmail) delete errors.email;
    setEditErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const payload = buildPayload(values);
    // The whole name now lives in first_name. If the record still carries an old
    // last_name, clear it — otherwise the list would show that half twice.
    if (hasCombinedName && String(values.last_name || "").trim()) {
      payload.last_name = "";
    }

    try {
      await instance.put(detailUrl(values.id), payload);
      showSnackbar(`${config.label} updated successfully.`, "success");
      cancelEdit();
      fetchList(page + 1, search);
    } catch (error) {
      console.error(`Error updating ${config.label}:`, error);
      const apiError =
        error?.response?.data?.error || error?.response?.data?.detail;
      showSnackbar(
        typeof apiError === "string" ? apiError : `Could not update ${config.label}.`,
        "error"
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await instance.delete(detailUrl(id));
      showSnackbar(`${config.label} deleted.`, "success");
      // If we just removed the last row on a page, step back a page.
      const lastOnPage = rows.length === 1 && page > 0;
      const targetPage = lastOnPage ? page - 1 : page;
      setPage(targetPage);
      fetchList(targetPage + 1, search);
    } catch (error) {
      console.error(`Error deleting ${config.label}:`, error);
      showSnackbar(`Could not delete ${config.label}.`, "error");
    }
  };

  // ---- field renderers -------------------------------------------------------

  const renderInput = (field, values, onChange, errors, showLabel = false) => {
    if (field.type === "boolean") {
      return (
        <Box className={classes.switchRow}>
          <Switch
            color="primary"
            checked={Boolean(values[field.key])}
            onChange={(e) => onChange(field.key, e.target.checked)}
          />
          <Typography variant="body2">
            {values[field.key] ? "Active" : "Inactive"}
          </Typography>
        </Box>
      );
    }
    if (field.type === "date") {
      return (
        <DatePickerACCB
          label={showLabel ? field.label : ""}
          value={values[field.key] ? parseDateString(values[field.key]) : null}
          onChange={(val) => onChange(field.key, val)}
          required={field.required}
          error={Boolean(errors[field.key])}
          helperText={errors[field.key] || " "}
          InputProps={{ style: { fontSize: '0.8rem' } }}
          InputLabelProps={{ style: { fontSize: '0.8rem' } }}
          maxDate={new Date()}
        />
      );
    }
    if (field.type === "select") {
      return (
        <TextField
          select
          InputProps={{ style: { fontSize: '0.8rem' } }}
          InputLabelProps={{ style: { fontSize: '0.8rem' } }}
          fullWidth
          size="small"
          variant="outlined"
          label={showLabel && field.label}
          required={field.required}
          value={values[field.key] ?? ""}
          onChange={(e) => onChange(field.key, e.target.value)}
          error={Boolean(errors[field.key])}
          helperText={errors[field.key] || " "}
        >
          {(field.options
            ? field.options.map((opt) => ({ id: opt.value, name: opt.label }))
            : categories
          ).map((cat) => (
            <MenuItem key={cat.id} value={cat.id} style={{ fontSize: '0.8rem' }}>
              {cat.name}
            </MenuItem>
          ))}
        </TextField>
      );
    }
    const rawValue = values[field.key];
    return (
      <TextField
        style={{ minWidth: '40px' }}
        InputProps={{ style: { fontSize: '0.8rem' } }}
        InputLabelProps={{ style: { fontSize: '0.8rem' } }}
        fullWidth
        size="small"
        variant="outlined"
        label={showLabel && field.label}
        required={field.required}
        value={Array.isArray(rawValue) ? rawValue.join(", ") : rawValue ?? ""}
        onChange={(e) => onChange(field.key, e.target.value)}
        error={Boolean(errors[field.key])}
        helperText={errors[field.key] || " "}
        inputProps={field.type === "phone" ? { maxLength: 10 } : undefined}
      />
    );
  };

  const renderReadOnly = (field, row) => {
    if (field.type === "date") {
      return safeDateFormat(row[field.key]) || "—";
    }
    if (field.type === "boolean") {
      return (
        <Chip
          size="small"
          label={row[field.key] ? "Active" : "Inactive"}
          style={{
            background: row[field.key] ? "#ECFDF3" : "#F2F4F7",
            color: row[field.key] ? "#027A48" : "#475467",
            fontWeight: 600,
          }}
        />
      );
    }
    if (field.key === 'arbitator_category') {
      const catId = row.arbitator_category ?? row.arbitrator_category;
      const category = categories.find(c => String(c.id) === String(catId));
      if (category) return category.name;
      return row.arbitrator_category_name || row.arbitator_category || row.arbitrator_category || "—";
    }
    if (field.key === 'first_name')
      return (hasCombinedName ? fullName(row) : row?.user?.first_name) || "—";
    if (field.key === 'last_name')
      return row?.user?.last_name || "—";
    if (field.key === 'email')
      return displayEmail(row?.user?.email);
    if (field.key === 'bac_role')
      return row?.bac_role_name || "—";
    // Some fields (e.g. language) may come back as a list — flatten for display.
    if (Array.isArray(row[field.key]))
      return row[field.key].join(", ") || "—";
    return row[field.key] || "—";
  };

  // Distribute add-form columns responsively based on field count.
  const addColMd = Math.max(2, Math.floor(10 / config.fields.length));

  return (
    <Box className={classes.panel}>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        className={classes.header}
      >
        <Grid item>
          <Typography className={classes.title}>
            {config.label} Master
          </Typography>
        </Grid>
        <Grid item>
          <Box display="flex" alignItems="center" style={{ gap: 12 }}>
            {isArbitrator && (
              <TextField
                select
                size="small"
                variant="outlined"
                label="Category"
                className={classes.filter}
                value={categoryFilter}
                onChange={(e) => handleCategoryFilterChange(e.target.value)}
                InputProps={{ style: { fontSize: "0.8rem" } }}
                InputLabelProps={{ style: { fontSize: "0.8rem" } }}
              >
                <MenuItem value="all" style={{ fontSize: "0.8rem" }}>
                  All
                </MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id} style={{ fontSize: "0.8rem" }}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <GlobalSearch
              value={search}
              onSearchChange={handleSearchChange}
              placeholder={`Search ${config.label}s...`}
            />
          </Box>
        </Grid>
      </Grid>

      <Paper className={classes.paper}>
        {/* Add row */}
        <Grid
          container
          spacing={2}
          alignItems="flex-start"
          className={classes.addForm}
        >
          {config.fields.map((field) => (
            <Grid
              item
              xs={12}
              sm={field.type === "boolean" ? 12 : 6}
              md={field.md || addColMd}
              key={field.key}
            >
              {renderInput(
                field,
                newRow,
                (key, value) => {
                  setNewRow((prev) => ({ ...prev, [key]: value }));
                  if (addErrors[key])
                    setAddErrors((prev) => ({ ...prev, [key]: "" }));
                },
                addErrors,
                true,
              )}
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant="contained"
              color="primary"
              className={classes.addButton}
              endIcon={<span style={{ fontSize: 18, lineHeight: 1 }}>+</span>}
              disabled={submitting}
              onClick={handleAdd}
              size="small"
            >
              {submitting ? "Adding…" : "Add"}
            </Button>
          </Grid>
        </Grid>

        {/* List */}
        {loading ? (
          <CircularCenteredLoader />
        ) : rows.length === 0 ? (
          <Box className={classes.emptyState}>
            <Typography variant="body1">No {config.label} records yet.</Typography>
            <Typography variant="body2">
              Add one using the form above to get started.
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table size="small" style={{ maxWidth: '100%', marginTop: 0 }}>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell}>S.No.</TableCell>
                    {config.fields.map((f) => (
                      <TableCell
                        key={f.key}
                        className={classes.headCell}
                        sortDirection={f.key === "age" && ageSort ? ageSort : false}
                      >
                        {f.key === "age" ? (
                          <TableSortLabel
                            className={classes.sortLabel}
                            active={Boolean(ageSort)}
                            direction={ageSort || "asc"}
                            onClick={handleAgeSortToggle}
                          >
                            {f.label}
                          </TableSortLabel>
                        ) : (
                          f.label
                        )}
                      </TableCell>
                    ))}
                    <TableCell className={classes.headCell} align="center">
                      Modify
                    </TableCell>
                    <TableCell className={classes.headCell} align="center">
                      Delete
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row, rowIndex) => {
                    const isEditing = editId === row.id;
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell className={classes.cell}>{page * ROWS_PER_PAGE + rowIndex + 1}</TableCell>
                        {config.fields.map((field) => (
                          <TableCell key={field.key} className={classes.cell}>
                            {isEditing
                              ? renderInput(
                                field,
                                editRow,
                                (key, value) => {
                                  setEditRow((prev) => ({
                                    ...prev,
                                    [key]: value,
                                  }));
                                  if (editErrors[key])
                                    setEditErrors((prev) => ({
                                      ...prev,
                                      [key]: "",
                                    }));
                                },
                                editErrors,
                              )
                              : renderReadOnly(field, row)}
                          </TableCell>
                        ))}
                        <TableCell align="center">
                          {isEditing ? (
                            <Box
                              display="flex"
                              justifyContent="center"
                              style={{ gap: 8 }}
                            >
                              <IconButton
                                size="small"
                                className={classes.actionBtn}
                                onClick={saveEdit}
                                title="Save"
                              >
                                Save
                              </IconButton>
                              <IconButton
                                size="small"
                                className={classes.actionBtn}
                                onClick={cancelEdit}
                                title="Cancel"
                              >
                                <Close style={{ height: 18, width: 18 }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <IconButton
                              size="small"
                              title="Modify"
                              aria-label="Modify"
                              className={classes.iconActionBtn}
                              onClick={() => startEdit(row)}
                            >
                              <Edit style={{ height: 18, width: 18 }} />
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            title="Delete"
                            aria-label="Delete"
                            className={classes.iconActionBtn}
                            disabled={isEditing}
                            onClick={() => handleDelete(row.id)}
                          >
                            <DeleteOutlineIcon
                              style={{ height: 18, width: 18 }}
                            />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box display="flex" justifyContent="flex-end">
              <TablePagination
                component="div"
                count={count}
                page={page}
                onChangePage={handleChangePage}
                rowsPerPage={ROWS_PER_PAGE}
                rowsPerPageOptions={[]}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default BacMasterPanel;
